"use client";

import {
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type ViewportOptions,
} from "framer-motion";
import type { PropsWithChildren } from "react";
import {
  getPublicEntranceTransition,
  PUBLIC_ENTRANCE_REDUCED_VARIANTS,
  PUBLIC_ENTRANCE_VARIANTS,
  type PublicEntranceVariant,
} from "./publicEntranceConfig";

const DEFAULT_IN_VIEW_VIEWPORT: ViewportOptions = {
  once: true,
  amount: 0.12,
  margin: "0px 0px -56px 0px",
};

/** Homepage sections wait until they sit a bit higher in the viewport. */
export const HOME_SECTION_REVEAL_VIEWPORT: ViewportOptions = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -20% 0px",
};

export interface PublicEntranceProps extends PropsWithChildren {
  /** Stagger delay in seconds. */
  delay?: number;
  variant?: PublicEntranceVariant;
  /** When true, animates once when scrolled into view instead of on mount. */
  inView?: boolean;
  viewport?: ViewportOptions;
  className?: string;
  style?: HTMLMotionProps<"div">["style"];
  as?: "div" | "section" | "article";
}

const MotionTags = {
  div: motion.div,
  section: motion.section,
  article: motion.article,
} as const;

export function PublicEntrance({
  children,
  delay = 0,
  variant = "group",
  inView = false,
  viewport,
  className,
  style,
  as = "div",
}: PublicEntranceProps) {
  const reduceMotion = useReducedMotion();
  const MotionComponent = MotionTags[as];
  const variants = reduceMotion
    ? PUBLIC_ENTRANCE_REDUCED_VARIANTS
    : PUBLIC_ENTRANCE_VARIANTS[variant];
  const transition = getPublicEntranceTransition(variant, delay);

  if (inView) {
    return (
      <MotionComponent
        className={className}
        style={style}
        initial="hidden"
        whileInView="visible"
        viewport={viewport ?? DEFAULT_IN_VIEW_VIEWPORT}
        variants={variants}
        transition={transition}
      >
        {children}
      </MotionComponent>
    );
  }

  return (
    <MotionComponent
      className={className}
      style={style}
      initial="hidden"
      animate="visible"
      variants={variants}
      transition={transition}
    >
      {children}
    </MotionComponent>
  );
}
